"use client";

import React, { useEffect, useMemo, useState } from "react";
import AniCombobox from "../primitives/AniCombobox";
import AniIcon from "../primitives/AniIcon";
import AniButton from "../primitives/AniButton";
import DaysCells, { type DaysCellState } from "../primitives/DaysCells";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

type Cell = { date: Date; outside: boolean };

/** Keep six rows so the card and every 32px cell stay fixed like the Figma. */
function buildGrid(year: number, month: number): Cell[] {
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate() + index,
    );
    return { date, outside: date.getMonth() !== month };
  });
}

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Seeded with the date from the Figma frame so the server and the client agree
 * on the first paint; the real date is picked up on mount. Deriving it at
 * render time instead would hydrate differently whenever the server's timezone
 * rolls the month before the visitor's does.
 */
const SEED_DATE = new Date(2026, 6, 15);

const AniCalendar = () => {
  const [today, setToday] = useState(SEED_DATE);
  const [view, setView] = useState(() => ({
    year: SEED_DATE.getFullYear(),
    month: SEED_DATE.getMonth(),
  }));
  const [selected, setSelected] = useState<Date | null>(null);

  useEffect(() => {
    const now = new Date();
    setToday(now);
    setView({ year: now.getFullYear(), month: now.getMonth() });
  }, []);

  const cells = useMemo(() => buildGrid(view.year, view.month), [view]);
  const years = useMemo(() => {
    const base = view.year;
    return Array.from({ length: 7 }, (_, index) => base - 3 + index);
  }, [view.year]);

  const shift = (delta: number) =>
    setView((current) => {
      const next = new Date(current.year, current.month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });

  return (
    <div
      data-token="Ani Calendar"
      data-pad="sm"
      className="ani-calendar ani-tint"
      data-inspect-tokens="--ani-calendar-base-background --ani-calendar-border-radius"
    >
      <div className="ani-calendar-head" data-structure="Calendar header" data-gap="spacing/xs">
        <AniCombobox
          wide
          label="Month"
          value={String(view.month)}
          onChange={(next) => setView((c) => ({ ...c, month: Number(next) }))}
          options={MONTHS.map((month, index) => ({
            value: String(index),
            label: month,
          }))}
        />
        <AniCombobox
          label="Year"
          value={String(view.year)}
          onChange={(next) => setView((c) => ({ ...c, year: Number(next) }))}
          options={years.map((year) => ({
            value: String(year),
            label: String(year),
          }))}
        />
        <span className="ani-calendar-nav">
          <button
            type="button"
            className="ani-icon-btn"
            aria-label="Previous month"
            onClick={() => shift(-1)}
          >
            <AniIcon name="caret-left" />
          </button>
          <button
            type="button"
            className="ani-icon-btn"
            aria-label="Next month"
            onClick={() => shift(1)}
          >
            <AniIcon name="caret-right" />
          </button>
        </span>
      </div>

      <div className="ani-weekdays" data-token="Ani Calendar/Weekly Cells">
        {WEEKDAYS.map((day) => (
          <span key={day} className="ani-weekday">
            {day}
          </span>
        ))}
      </div>

      <div
        className="ani-days" data-structure="Day cells · 7 columns" data-gap="spacing/xs rows · responsive columns"
        role="group"
        aria-label={`${MONTHS[view.month]} ${view.year}`}
      >
        {cells.map(({ date, outside }) => {
          const isToday = isSameDay(date, today);
          const isSelected = selected ? isSameDay(date, selected) : false;
          const state: DaysCellState = outside
            ? "Next Month"
            : isToday
                ? "Today"
                : "Default";
          return (
            <DaysCells
              key={date.toISOString()}
              state={state}
              selected={isSelected}
              ariaLabel={date.toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
              onClick={() => {
                setSelected(date);
                if (outside) {
                  setView({ year: date.getFullYear(), month: date.getMonth() });
                }
              }}
              label={String(date.getDate())}
            />
          );
        })}
      </div>

      <div className="ani-calendar-foot" data-structure="Calendar footer">
        <AniButton
          variant="tertiary"
          onClick={() => setSelected(null)}
        >
          Clear
        </AniButton>
        <AniButton
          variant="tertiary"
          onClick={() => {
            setView({ year: today.getFullYear(), month: today.getMonth() });
            setSelected(today);
          }}
        >
          Today
        </AniButton>
      </div>
    </div>
  );
};

export default AniCalendar;
